import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, IconButton, Popover, Typography } from "@mui/material";
import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { CalendarDate, getLocalTimeZone, startOfMonth, startOfWeek, today } from "@internationalized/date";
import { useSelector } from "react-redux";
import { ApiCall } from "../../helper/axios";
import HoverTooltip from "../../common_components/hoverOverlay/HoverTooltip";
import CommonLoader from "../../common_components/CommonLoader";
import { TooltipRows } from "./EmployeePerformance";
const MODULE_KEY = "employee_performance";
const FONT_FAMILY = "Inter, sans-serif";
const STATUS_META = {
    dont_clock: { label: "Don't Clock", bg: "#F5106E", color: "#fff" },
    not_correct_hours: { label: "Not Correct Hours", bg: "#F5A623", color: "#181D27" },
    paid_time_off: { label: "Leave", bg: "#7A2CCB", color: "#fff" },
};
const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const WEEKDAYS = ["S", "M", "T", "W", "T", "F", "S"];
const ymd = (d) => `${d.year}-${String(d.month).padStart(2, "0")}-${String(d.day).padStart(2, "0")}`;
/** 27000 -> "7h 30m", 1800 -> "30m" */
const formatDuration = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    if (h > 0 && m > 0)
        return `${h}h ${m}m`;
    if (h > 0)
        return `${h}h`;
    return `${m}m`;
};
export default function ClockifyCalendar({ open, anchorEl, onClose, userId, month }) {
    const token = useSelector((state) => state.auth.token);
    const [dayMap, setDayMap] = useState({});
    const [requiredHours, setRequiredHours] = useState(8);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const requestId = useRef(0); // ignores a slow response that a newer month pick overtook
    const [shown, setShown] = useState(() => startOfMonth(month ?? today(getLocalTimeZone())));
    const [pickingMonth, setPickingMonth] = useState(false);
    const [pickerYear, setPickerYear] = useState(shown.year);
    // Every time it opens, start on the page's month with the day grid showing.
    useEffect(() => {
        if (!open)
            return;
        const start = startOfMonth(month ?? today(getLocalTimeZone()));
        setShown(start);
        setPickerYear(start.year);
        setPickingMonth(false);
    }, [open, month]);
    const goTo = (next) => setShown(next);
    // Load the shown month's Clockify days whenever the calendar opens or the month changes.
    useEffect(() => {
        if (!open || !userId)
            return;
        const id = ++requestId.current;
        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await ApiCall("POST", "/api/employee-performance/clockify-calendar", { user_id: userId, month: `${String(shown.month).padStart(2, "0")}/${shown.year}` }, { Authorization: token || "", "x-module-key": MODULE_KEY });
                if (id !== requestId.current)
                    return;
                const body = response?.data ?? response?.response?.data;
                if (body?.success) {
                    const data = body.data;
                    if (typeof data?.required_hours === "number")
                        setRequiredHours(data.required_hours);
                    setDayMap(Object.fromEntries((data?.days || []).map((d) => [d.date, d])));
                    setError(data?.clockify?.available === false ? data.clockify.message || "Clockify hours are not available." : null);
                }
                else {
                    setDayMap({});
                    setError(body?.message || "Failed to load Clockify hours.");
                }
            }
            catch (e) {
                if (id !== requestId.current)
                    return;
                setDayMap({});
                setError(e?.message || "Failed to load Clockify hours.");
            }
            finally {
                if (id === requestId.current)
                    setLoading(false);
            }
        };
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, userId, shown.year, shown.month]);
    // 6 weeks from the Sunday on/before the 1st, so the grid height never jumps between months.
    const days = useMemo(() => {
        const first = startOfWeek(shown, "en-US");
        return Array.from({ length: 42 }, (_, i) => first.add({ days: i }));
    }, [shown]);
    const todayDate = today(getLocalTimeZone());
    const usedStatuses = Object.keys(STATUS_META);
    // Hover for a day, in the module's standard dark table tooltip: what was logged vs what is required.
    const dayTip = (date, day) => {
        const isLeave = day?.status === "paid_time_off";
        // Nothing logged and not a leave day (the red "Don't Clock" days included) -> no tooltip, there is nothing to show.
        if (!day || (day.seconds <= 0 && !isLeave))
            return null;
        const items = [];
        if (isLeave)
            items.push({ name: "Leave Type", value: day.leave_type || "PTO" });
        if (day.seconds > 0)
            items.push({ name: "Clocked Run", value: formatDuration(day.seconds) });
        // if (day.is_working_day) items.push({ name: "Required", value: `${requiredHours}h` });
        // if (day.status) items.push({ name: "Status", value: STATUS_META[day.status].label });
        return _jsx(TooltipRows, { heading: `${String(date.month).padStart(2, "0")}/${String(date.day).padStart(2, "0")}/${date.year}`, items: items });
    };
    return (_jsxs(Popover, { open: open, anchorEl: anchorEl, onClose: onClose, disableRestoreFocus // don't hand focus back to the Clockify button on close (it showed a pink focus highlight)
        : true, anchorOrigin: { vertical: "bottom", horizontal: "right" }, transformOrigin: { vertical: "top", horizontal: "right" }, slotProps: {
            paper: {
                sx: {
                    mt: 0.75, width: 380, maxWidth: "95vw", borderRadius: "16px", overflow: "hidden",
                    boxShadow: "0px 12px 16px -4px rgba(10, 13, 18, 0.08), 0px 4px 6px -2px rgba(10, 13, 18, 0.03)",
                    border: "1px solid #EAECF0",
                },
            },
        }, children: [_jsxs(Box, { sx: { display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, pt: 2, pb: 1 }, children: [_jsx(IconButton, { size: "small", "aria-label": "Previous month", disabled: pickingMonth, onClick: () => goTo(shown.subtract({ months: 1 })), sx: { color: "#717680" }, children: _jsx(ChevronLeft, { size: 20 }) }), _jsxs(Box, { onClick: () => { setPickerYear(shown.year); setPickingMonth((p) => !p); }, sx: {
                            display: "inline-flex", alignItems: "center", gap: 0.75, cursor: "pointer", userSelect: "none",
                            bgcolor: "#F5F5F5", borderRadius: "20px", px: 2, py: 0.75,
                        }, children: [_jsxs(Typography, { sx: { fontFamily: FONT_FAMILY, fontSize: "14px", fontWeight: 500, color: "#414651" }, children: [MONTH_NAMES[shown.month - 1], " ", shown.year] }), _jsx(ChevronDown, { size: 16, color: "#717680", style: { transition: "transform 0.2s", transform: pickingMonth ? "rotate(180deg)" : "none" } })] }), _jsx(IconButton, { size: "small", "aria-label": "Next month", disabled: pickingMonth, onClick: () => goTo(shown.add({ months: 1 })), sx: { color: "#717680" }, children: _jsx(ChevronRight, { size: 20 }) })] }), pickingMonth ? (
            /* Month / year picker */
            _jsxs(Box, { sx: { px: 2, pb: 2 }, children: [_jsxs(Box, { sx: { display: "flex", alignItems: "center", justifyContent: "space-between", py: 1 }, children: [_jsx(IconButton, { size: "small", "aria-label": "Previous year", onClick: () => setPickerYear((y) => y - 1), sx: { color: "#717680" }, children: _jsx(ChevronLeft, { size: 18 }) }), _jsx(Typography, { sx: { fontFamily: FONT_FAMILY, fontSize: "14px", fontWeight: 600, color: "#181D27" }, children: pickerYear }), _jsx(IconButton, { size: "small", "aria-label": "Next year", onClick: () => setPickerYear((y) => y + 1), sx: { color: "#717680" }, children: _jsx(ChevronRight, { size: 18 }) })] }), _jsx(Box, { sx: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1 }, children: MONTH_NAMES.map((name, i) => {
                            const selected = pickerYear === shown.year && i + 1 === shown.month;
                            return (_jsx(Box, { onClick: () => { goTo(new CalendarDate(pickerYear, i + 1, 1)); setPickingMonth(false); }, sx: {
                                    textAlign: "center", py: 1.25, borderRadius: "8px", cursor: "pointer", fontFamily: FONT_FAMILY, fontSize: "14px",
                                    fontWeight: selected ? 600 : 500,
                                    bgcolor: selected ? "#FF008E" : "#FFFAFD",
                                    color: selected ? "#fff" : "#18181B",
                                    "&:hover": { bgcolor: selected ? "#FF008E" : "#FFE4F2", color: selected ? "#fff" : "#FF008E" },
                                }, children: name.slice(0, 3) }, name));
                        }) })] })) : (_jsxs(_Fragment, { children: [_jsxs(Box, { sx: { position: "relative", px: 2, pb: 1.5 }, children: [loading && (_jsx(Box, { sx: {
                                    position: "absolute", inset: 0, zIndex: 2, bgcolor: "#fff", // solid, so the old month's days never show through
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    "& img": { display: "block", width: 100, height: 100 },
                                }, children: _jsx(CommonLoader, { containerClassName: "" }) })), _jsxs(Box, { sx: { display: "grid", gridTemplateColumns: "repeat(7, 1fr)", rowGap: 0.75, justifyItems: "center" }, children: [WEEKDAYS.map((d, i) => (_jsx(Box, { sx: { py: 1, fontFamily: FONT_FAMILY, fontSize: "13px", fontWeight: 500, color: "#717680" }, children: d }, i))), days.map((date) => {
                                        const inMonth = date.month === shown.month;
                                        const day = inMonth ? dayMap[ymd(date)] : undefined;
                                        const status = day?.status ?? undefined;
                                        const meta = status ? STATUS_META[status] : null;
                                        const isToday = date.compare(todayDate) === 0;
                                        const tip = dayTip(date, day);
                                        const cell = (_jsx(Box, { sx: {
                                                width: 38, height: 38, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
                                                fontFamily: FONT_FAMILY, fontSize: "15px", fontWeight: meta ? 500 : 400,
                                                color: meta ? meta.color : inMonth ? "#18181B" : "#D0D5DD",
                                                bgcolor: meta ? meta.bg : "transparent",
                                                cursor: tip ? "pointer" : "default", // pointer only where a hover tooltip exists
                                                ...(isToday && inMonth && !meta && { border: "1px solid #FF008E", color: "#FF008E" }),
                                            }, children: date.day }));
                                        return tip ? (_jsx(HoverTooltip, { title: tip, placement: "top", children: cell }, ymd(date))) : (_jsx(React.Fragment, { children: cell }, ymd(date)));
                                    })] })] }), error && (_jsx(Typography, { sx: { fontFamily: FONT_FAMILY, fontSize: "12px", color: "#B42318", textAlign: "center", px: 2, pb: 1 }, children: error })), _jsx(Box, { sx: { display: "flex", alignItems: "center", justifyContent: "center", gap: 2, flexWrap: "wrap", px: 2, py: 1.5, borderTop: "1px solid #EAECF0" }, children: usedStatuses.map((s) => (_jsxs(Box, { sx: { display: "inline-flex", alignItems: "center", gap: 0.75 }, children: [_jsx(Box, { sx: { width: 10, height: 10, borderRadius: "50%", bgcolor: STATUS_META[s].bg } }), _jsx(Typography, { sx: { fontFamily: FONT_FAMILY, fontSize: "13px", color: "#414651" }, children: STATUS_META[s].label })] }, s))) })] }))] }));
}
